import AccordionItem from '@atoms/AccordionItem/AccordionItem';
import Icon from '@atoms/Icon/Icon';
import Text from '@atoms/Text/Text';
import { ICountry, ICountrySelector, IRegion } from '@models/ICountrySelector';
import Accordion from '@molecules/Accordion/Accordion';
import { useEffect, useState } from 'react';

const CountrySelector = ({
  activeCountry,
  languageSelectorLabel,
  countrySelectorLabel,
  countrySelectorPlaceholder,
  languageSelector,
}: ICountrySelector) => {
  const [searchInput, setSearchInput] = useState<string>('');
  const [activeRegions, setActiveRegions] = useState<IRegion[]>([]);
  const [filteredRegions, setFilteredRegions] = useState<IRegion[]>([]);
  const [allCountries, setAllCountries] = useState<IRegion[]>([]);

  const handleSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    const updatedFilteredRegions = activeRegions.map(({ name, countries }) => ({
      show: true,
      name,
      countries: countries.filter(({ name }) =>
        name.toLowerCase().includes(searchInput.toLowerCase()),
      ),
    }));
    setFilteredRegions(updatedFilteredRegions);
  };

  // Filter regions and countries to remove items where 'show' is false
  useEffect(() => {
    const filteredRegions: IRegion[] = languageSelector.regions.reduce(
      (acc: IRegion[], { name, countries, show }: IRegion) => {
        if (show) {
          const filteredCountries = countries.filter(
            ({ show }: ICountry) => show,
          );
          if (filteredCountries.length > 0) {
            acc.push({ name, countries: filteredCountries, show });
          }
        }
        return acc;
      },
      [],
    );

    setActiveRegions(filteredRegions);
    setFilteredRegions(filteredRegions);
    setAllCountries(filteredRegions);
  }, [languageSelector]);

  useEffect(() => {
    const filteredRegions: IRegion[] = activeRegions.reduce(
      (acc: IRegion[], { name, countries }: IRegion) => {
        const filteredRegions = countries.filter(({ name }: ICountry) =>
          name.toLowerCase().includes(searchInput.toLowerCase()),
        );

        if (filteredRegions.length > 0) {
          acc.push({ name, countries: filteredRegions, show: true });
        }

        return acc;
      },
      [],
    );

    const hasFilteredRegions = filteredRegions.some(
      ({ countries }) => countries.length > 0,
    );

    if (hasFilteredRegions) {
      setFilteredRegions(filteredRegions);
    } else {
      setFilteredRegions(allCountries);
    }
  }, [searchInput, activeRegions, allCountries]);

  if (!languageSelector) return null;

  const handleClickTracking = (
    activeCountry?: ICountry,
    languageName?: string,
    countryName?: string,
  ) => {
    const getActiveLanguage = activeCountry?.languages.find(
      (language) => language.isActive,
    );

    window.dataLayer.push({
      event: 'trackingEvent_navigation_country_selector_clicks',
      event_name_variable: 'navigation_country_selector_clicks',
      trackingEvent_navigation_country_selector_clicks: {
        previous_country: activeCountry?.name,
        previous_language: getActiveLanguage?.name,
        country_selected: countryName,
        language_selected: languageName,
      },
    });
  };

  return (
    <div className="tw-mt-sm tw-px-xs tw-py-xl md:tw-px-md">
      {activeCountry && activeCountry.languages.length > 1 && (
        <div className="tw-mb-xl tw-flex tw-flex-col tw-gap-xs">
          <Text variant="heading-21" className="tw-font-light tw-text-grey-100">
            {languageSelectorLabel}
          </Text>
          <div className="tw-flex tw-gap-2.5">
            {activeCountry.languages.map(({ name, url }) => (
              <a
                key={name}
                href={url}
                onClick={() =>
                  handleClickTracking(activeCountry, name, activeCountry.name)
                }
              >
                <Text
                  variant="body"
                  className="tw-font-light tw-text-grey-100 tw-underline hover:tw-no-underline"
                >
                  {name}
                </Text>
              </a>
            ))}
          </div>
        </div>
      )}
      <Text variant="heading-21" className="tw-font-light tw-text-grey-100">
        {countrySelectorLabel}
      </Text>
      <div className="tw-my-md tw-w-full">
        <form onSubmit={handleSubmit}>
          <div className="tw-group tw-flex tw-w-full tw-flex-col tw-rounded tw-border tw-border-grey-60 tw-bg-transparent tw-px-xs tw-py-4 tw-text-sm tw-font-light focus-within:tw-border-black">
            <label htmlFor="countrySearch" className="tw-sr-only">
              {countrySelectorLabel}
            </label>
            <div className="tw-flex tw-gap-0.5">
              <input
                type="text"
                name="countrySearch"
                id="countrySearch"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={countrySelectorPlaceholder}
                className="tw-w-full tw-grow tw-bg-transparent tw-outline-none"
              />
              <Icon
                name="magnifier"
                className="tw-ml-4 tw-fill-grey-70 tw-text-lg"
                size="1.5rem"
              />
            </div>
          </div>
        </form>
      </div>
      <div className="tw-border-t tw-border-grey-20">
        <Accordion>
          {filteredRegions.map(({ name, countries }) => (
            <AccordionItem
              key={name}
              title={name}
              variant="noStyle"
              isOpen={
                filteredRegions.length === 0 ||
                (countries.length > 0 && searchInput !== '') ||
                countries.some(
                  ({ name: countryName }) =>
                    countryName === activeCountry?.name,
                )
              }
            >
              <ul>
                {countries.map(({ name, image, languages }) => (
                  <li
                    key={name}
                    className="tw-flex tw-justify-between tw-border-t tw-border-grey-20 tw-py-xs"
                  >
                    <div className="tw-flex tw-gap-2.5">
                      <img
                        src={image}
                        alt={name}
                        className="tw-h-sm tw-w-sm tw-rounded-full tw-object-cover"
                      />
                      <Text variant="body" className="text-grey-100">
                        {name}
                      </Text>
                    </div>
                    <div className="tw-flex tw-gap-2.5">
                      {languages.map(({ name: languageName, url }) => (
                        <a
                          key={languageName}
                          href={url}
                          onClick={() =>
                            handleClickTracking(
                              activeCountry,
                              languageName,
                              name,
                            )
                          }
                        >
                          <Text
                            variant="body"
                            className="tw-font-light tw-text-grey-100 tw-underline hover:tw-no-underline"
                          >
                            {languageName}
                          </Text>
                        </a>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  );
};

export default CountrySelector;
